// ═══════════════════════════════════════════════════════════
// realtimeSync — слухач змін в _meta/sync документі
// ONE onSnapshot per session → точковий рефетч тільки змінених колекцій
// Не робить зайвих reads коли нічого не змінилось
// ═══════════════════════════════════════════════════════════
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

// Debounce — single user action often triggers a burst of related writes
// (e.g. saveAppointment → appointments + transactions + stock_movements
// + audit_log within ~200ms). Without coalescing, every snapshot tick
// fires its own refetch chain × N tabs. This window collapses bursts
// into one refetch per affected collection. 500ms is short enough that
// cross-tab visibility still feels live, long enough to catch typical
// multi-write user actions.
const COALESCE_MS = 500;

/**
 * Підписується на _meta/sync документ тенанта.
 * При змінах — викликає onCollectionsChanged([colName, ...])
 * Перший snapshot ігнорується (ініціалізація стану).
 *
 * Bursts of changes within COALESCE_MS are batched into a single
 * onCollectionsChanged call with the union of affected collections.
 *
 * @returns {Function} unsub — виклик скасовує підписку
 */
export function initRealtimeSync(tenantId, onCollectionsChanged) {
  if (!tenantId) return () => {};

  const syncRef = doc(db, `tenants/${tenantId}/_meta/sync`);
  let isFirst = true;
  let lastTs = {};

  // Coalesce buffer — collections that changed within the window.
  let pendingCols = new Set();
  let coalesceTimer = null;
  const flush = () => {
    coalesceTimer = null;
    if (pendingCols.size === 0) return;
    const cols = Array.from(pendingCols);
    pendingCols = new Set();
    onCollectionsChanged(cols);
  };

  const unsub = onSnapshot(
    syncRef,
    (snap) => {
      if (!snap.exists()) {
        isFirst = false;
        return;
      }
      const data = snap.data();

      if (isFirst) {
        // Просто запам'ятовуємо поточний стан, не тригеримо рефетч
        lastTs = { ...data };
        isFirst = false;
        return;
      }

      // Знаходимо колекції з новішим timestamp
      const changed = Object.entries(data)
        .filter(([col, ts]) => !lastTs[col] || ts > lastTs[col])
        .map(([col]) => col);

      lastTs = { ...data };

      if (changed.length === 0) return;

      for (const col of changed) pendingCols.add(col);
      if (coalesceTimer) clearTimeout(coalesceTimer);
      coalesceTimer = setTimeout(flush, COALESCE_MS);
    },
    (err) => {
      console.warn('[realtimeSync] onSnapshot error:', err);
    }
  );

  return () => {
    if (coalesceTimer) {
      clearTimeout(coalesceTimer);
      coalesceTimer = null;
    }
    pendingCols = new Set();
    unsub();
  };
}
